import { solveProofOfWork } from './powClient';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class NslMailAPI {

    constructor(accessToken, loadingFunc) {
        this.accessToken = accessToken;
        this.openCallCount = 0;
        this.loadingFunc = loadingFunc;
        if (!this.loadingFunc)
            this.loadingFunc = () => {}
    }

    beginCall() {
        this.openCallCount ++;
        this.loadingFunc(true);
    }

    endCall() {
        if (--this.openCallCount === 0)
            this.loadingFunc(false);
    }

    async api_get(path) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + path, {
                headers: {'Authorization': 'Bearer ' + this.accessToken}
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    async api_post(path, body) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + path, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + this.accessToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    async api_put(path, body) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + path, {
                method: 'PUT',
                headers: {
                    'Authorization': 'Bearer ' + this.accessToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }    

    async api_delete(path, body) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + path, {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + this.accessToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }    

    async signup(userData, onProgress) {
        this.beginCall();
        try {
            let pow = await solveProofOfWork(API_BASE_URL, 'signup', onProgress);
            let response = await fetch(API_BASE_URL + "/signup", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ...userData, ...pow })
            });
            let data = await response.json();
            if (response.status === 201) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    async login(username, password) {
        let formData = new FormData();
        formData.append('username', username);
        formData.append('password', password);

        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + "/authenticate", {
                method: 'POST',
                body: formData
            });

            let data = {};
            try {
                data = await response.json();
            } catch (e) {
                // Empty or non-JSON body; fall through to the generic message.
            }

            if (response.status === 200) {
                this.accessToken = data.access_token;
                return data.access_token;
            }

            // Surface the server's message so the caller can distinguish a bad
            // password from an account awaiting approval.
            let error = new Error(data.message || 'Invalid username or password');
            error.code = data.code;
            throw error;
        } finally {
            this.endCall();
        }
    }

    async getUserProfile() {
        let user = await this.api_get('/users/me');
        return user;
    }

    async getNewMessageCount() {
        let result = await this.api_get('/users/me/new-message-count');
        return result.count;
    }

    async getAliasesHeldCount() {
        let result = await this.api_get('/users/me/aliases-held-count');
        return result.count;
    }

    async saveUserProfile(profile) {
        let result = await this.api_put(`/users/${profile.id}`, profile);
        return result;
    }

    async getUserEmailAddresses(userId) {
        let addresses = await this.api_get(`/users/${userId}/email-addresses`);
        return addresses;
    }

    async addUserEmailAddress(userId, address) {
        let result = await this.api_post(`/users/${userId}/email-addresses`, {
            "address": address
        });
        return result;
    }

    async verifyUserEmailAddress(userId, addressId) {
        let result = await this.api_post(`/users/${userId}/email-addresses/${addressId}/send-verification`)
        return result;
    }

    async deleteUserEmailAddress(userId, addressId) {
        let result = await this.api_delete(`/users/${userId}/email-addresses/${addressId}`);
        return result;
    }

    async getUserAliases(filter, search, sort, dir) {
        let aliases = await this.api_get(`/aliases?filter=${filter}&search=${search}&sort=${sort}&dir=${dir}`);
        return aliases;
    }

    async getAlias(aliasId) {
        let alias = await this.api_get(`/aliases/${aliasId}`);
        return alias;
    }

    async createAlias(aliasData) {
        let result = await this.api_post(`/aliases`, aliasData);
        return result;
    }

    async saveAlias(alias) {
        let result = await this.api_put(`/aliases/${alias.id}`, alias);
        return result;
    }

    async getWhitelistRules(aliasId) {
        let rules = await this.api_get(`/aliases/${aliasId}/whitelist-rules`);
        return rules;        
    }

    async deleteWhitelistRule(aliasId, ruleId) {
        let result = await this.api_delete(`/aliases/${aliasId}/whitelist-rules/${ruleId}`);
        return result;
    }

    async getBlacklistRules(aliasId) {
        let rules = await this.api_get(`/aliases/${aliasId}/blacklist-rules`);
        return rules;        
    }

    async deleteBlacklistRule(aliasId, ruleId) {
        let result = await this.api_delete(`/aliases/${aliasId}/blacklist-rules/${ruleId}`);
        return result;
    }

    async getAddressMap(aliasId) {
        let map = await this.api_get(`/aliases/${aliasId}/address-map`);
        return map;        
    }

    async getHeldMessages(aliasId) {
        let held = await this.api_get(`/aliases/${aliasId}/held-messages`);
        return held;
    }

    async allowHeldMessage(aliasId, messageId, domains) {
        let result = await this.api_post(`/aliases/${aliasId}/held-messages/${messageId}/allow`, {
            "domains": domains
        });
        return result;
    }

    async blockHeldMessage(aliasId, messageId, domains) {
        let result = await this.api_post(`/aliases/${aliasId}/held-messages/${messageId}/block`, {
            "domains": domains
        });
        return result;
    }

    async deleteHeldMessage(aliasId, messageId) {
        let result = await this.api_post(`/aliases/${aliasId}/held-messages/${messageId}/delete`, {});
        return result;
    }

    async refreshHeldCount(aliasId) {
        let result = await this.api_post(`/aliases/${aliasId}/refresh-held-count`, {});
        return result;
    }

    async forgotPassword(email) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + "/forgot-password", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: email })
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    async resetPassword(authId) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + `/reset-password/${authId}`, {
                method: 'GET'
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    async verifyEmail(code) {
        this.beginCall();
        try {
            let response = await fetch(API_BASE_URL + `/verify-address/${code}`, {
                method: 'GET'
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    async submitContactForm(formData, onProgress) {
        this.beginCall();
        try {
            let headers = {
                'Content-Type': 'application/json'
            };
            if (this.accessToken) {
                headers['Authorization'] = 'Bearer ' + this.accessToken;
            }
            let pow = await solveProofOfWork(API_BASE_URL, 'contact', onProgress);
            let response = await fetch(API_BASE_URL + "/contact", {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ ...formData, ...pow })
            });
            let data = await response.json();
            if (response.status === 200) {
                return data;
            } else {
                throw new Error(data.message);
            }
        } finally {
            this.endCall();
        }
    }

    // Mail API methods
    // A page of the inbox, newest first. Returns the whole envelope --
    // { messages, total, unread, offset, limit } -- because the caller needs
    // total to know whether an older page exists.
    async getMessages(offset = 0) {
        return await this.api_get(`/users/me/messages?offset=${offset}`);
    }

    async getMessage(messageId) {
        let message = await this.api_get(`/users/me/messages/${messageId}`);
        return message;
    }

    async deleteMessage(messageId) {
        let result = await this.api_delete(`/users/me/messages/${messageId}`);
        return result;
    }

    async sendReply(originalMessageId, subject, body) {
        let result = await this.api_post('/users/me/messages', {
            original_message_id: originalMessageId,
            subject: subject,
            body: body
        });
        return result;
    }

}

export default NslMailAPI;